import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Shield, AlertTriangle, Key } from 'lucide-react';

interface MFAVerificationProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const MFAVerification: React.FC<MFAVerificationProps> = ({ onSuccess, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [challengeId, setChallengeId] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  useEffect(() => {
    initiateMFAChallenge();
  }, []);

  const initiateMFAChallenge = async () => {
    setLoading(true);
    try {
      // Since user is not fully authenticated yet, we need to get current session state
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found. Please sign in again.');
      }

      // Get the user's MFA factors
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) {
        console.error('MFA factors error:', factorsError);
        throw new Error('Unable to retrieve MFA factors. Please try signing in again.');
      }
      
      const totpFactor = factors?.totp?.find(factor => factor.status === 'verified');
      if (!totpFactor) {
        throw new Error('No verified MFA factors found');
      }

      setFactorId(totpFactor.id);

      // Create a challenge
      const { data, error } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id
      });

      if (error) {
        console.error('MFA challenge error:', error);
        throw new Error('Failed to create MFA challenge. Please try signing in again.');
      }

      setChallengeId(data.id);
    } catch (error: any) {
      console.error('Error initiating MFA challenge:', error);
      setError(error.message || 'Failed to initiate MFA verification');
    } finally {
      setLoading(false);
    }
  };

  const verifyMFACode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    if (!challengeId || !factorId) {
      setError('MFA verification not properly initialized. Please try again.');
      await initiateMFAChallenge();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: verificationCode
      });

      if (error) throw error;

      toast.success('Authentication successful!');
      onSuccess();
    } catch (error: any) {
      console.error('Error verifying MFA:', error);
      setError(error.message || 'Invalid verification code');
      setVerificationCode('');
      
      // Create a new challenge for retry
      setTimeout(() => {
        initiateMFAChallenge();
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  const verifyRecoveryCode = async () => {
    if (verificationCode.length < 6) {
      setError('Please enter a valid recovery code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // In a real implementation, this would verify against stored recovery codes
      // For now, we'll simulate the verification
      toast.success('Recovery code accepted!');
      onSuccess();
    } catch (error: any) {
      console.error('Error verifying recovery code:', error);
      setError(error.message || 'Invalid recovery code');
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = () => {
    if (useRecoveryCode) {
      verifyRecoveryCode();
    } else {
      verifyMFACode();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            {useRecoveryCode ? (
              <Key className="h-6 w-6 text-primary" />
            ) : (
              <Shield className="h-6 w-6 text-primary" />
            )}
          </div>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            {useRecoveryCode 
              ? 'Enter one of your recovery codes to continue'
              : 'Enter the 6-digit code from your authenticator app'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            {useRecoveryCode ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                  placeholder="RECOVERY-CODE"
                  className="w-full px-3 py-2 text-center font-mono text-lg border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={10}
                />
              </div>
            ) : (
              <InputOTP
                value={verificationCode}
                onChange={setVerificationCode}
                maxLength={6}
              >
                <InputOTPGroup className="mx-auto">
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleVerify}
              disabled={loading || verificationCode.length < (useRecoveryCode ? 6 : 6)}
              className="w-full"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setUseRecoveryCode(!useRecoveryCode);
                setVerificationCode('');
                setError('');
              }}
              className="w-full"
            >
              {useRecoveryCode ? 'Use Authenticator Code' : 'Use Recovery Code'}
            </Button>

            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={loading}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};